import { CircleExclamationFilledIcon } from '@/src/components/icon/filled/CircleExclamationFilledIcon';
import { CircleInformationFilledIcon } from '@/src/components/icon/filled/CircleInformationFilledIcon';
import { WarningFilledIcon } from '@/src/components/icon/filled/WarningFilledIcon';
import { Box, BoxProps, SvgIconProps, Typography, TypographyProps, useTheme } from '@mui/material';
import React, { ElementType, ComponentType, forwardRef, ReactNode, useMemo } from 'react';
import { getFocusRingStyle } from '@/src/components/focusring/focusring';

export interface BannerProps extends BoxProps<ElementType> {
  /**
   * Defines which variant of Banner component should be rendered
   * @default info
   */
  variant?: 'info' | 'warning' | 'error';
  /**
   * Set the proportions
   * @default large
   */
  size?: 'large' | 'small';
  /**
   * Properties passed to indicator icon
   */
  iconProps?: SvgIconProps;
  /**
   * Icon node to be used
   */
  IconComponent?: ComponentType<SvgIconProps>;
  /**
   * Properties passed to wrapper of children
   */
  typographyProps?: TypographyProps<ElementType>;
  /**
   * Properties applied to wrapper of content (Icon and Typography)
   */
  wrapperProps?: BoxProps<ElementType>;
  /**
   * ReactNode that renders buttons. If multiple buttons are needed, wrap them in React.Fragment
   */
  buttons?: ReactNode;
  /**
   * Properties applied to wrapper element of buttons
   */
  buttonsWrapperProps?: BoxProps<ElementType>;
}

/**
 * Alert notification banner
 */
export const Banner: React.FC<BannerProps> = forwardRef(
  (
    {
      children,
      variant = 'info',
      size = 'large',
      sx,
      iconProps,
      IconComponent,
      typographyProps,
      wrapperProps,
      buttons,
      buttonsWrapperProps,
      onClick,
      ...rest
    },
    ref,
  ) => {
    const theme = useTheme();
    const { sx: iconSx, ...restIcon } = iconProps ?? {};
    const { sx: typographySx, ...restTypography } = typographyProps ?? {};
    const { sx: wrapperSx, ...restWrapper } = wrapperProps ?? {};
    const { sx: buttonsWrapperSx, ...restButtonsWrapper } = buttonsWrapperProps ?? {};

    const [borderColor, backgroundColor, IconVariantComponent] = useMemo(() => {
      switch (variant) {
        case 'error':
          return ['error.main', 'error.light', WarningFilledIcon];
        case 'warning':
          return ['warning.main', 'warning.light', CircleExclamationFilledIcon];
        case 'info':
        default:
          return ['info.main', 'info.light', CircleInformationFilledIcon];
      }
    }, [variant]);
    const BannerIcon = IconComponent ?? IconVariantComponent;

    return (
      <Box
        data-testid="Banner"
        role="alert"
        aria-atomic="true"
        aria-live="assertive"
        onClick={onClick}
        sx={{
          display: 'flex',
          width: '100%',
          // on mobile content and button bellow each other
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          // proportions
          px: 2,
          py: size === 'small' ? 1 : { xs: 2, md: 1 },
          gap: 1,
          // style
          borderLeft: 6,
          borderColor: borderColor,
          backgroundColor: theme.palette.mode === 'dark' ? 'onSurface.os900' : backgroundColor,
          textAlign: 'left',
          // clickable style
          ...(onClick && {
            position: 'relative',
            cursor: 'pointer',
            // focused style
            ...getFocusRingStyle(theme, true, '2px'),
          }),
          ...sx,
        }}
        ref={ref}
        {...rest}
      >
        <Box
          data-testid="Banner.wrapper"
          sx={{
            // align icon and content
            flexGrow: '1',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
            gap: 1,
            py: 0.5,
            ...wrapperSx,
          }}
          {...restWrapper}
        >
          <BannerIcon
            data-testid="Banner.icon"
            sx={{
              color: borderColor,
              ...iconSx,
            }}
            {...restIcon}
          />
          <Typography
            data-testid="Banner.text"
            sx={{
              color: 'text.primary',
              flexGrow: '1',
              // custom font according to Figma
              fontSize: { xl: '16px' },
              lineHeight: { xl: '24px' },
              ...typographySx,
            }}
            {...restTypography}
          >
            {children}
          </Typography>
        </Box>
        {buttons && (
          <Box
            data-testid="Banner.buttons"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              // always show buttons next to each other
              flexShrink: '0',
              // on mobile align with text
              ml: { xs: 4, md: 0 },
              // on mobile move to the right side
              alignSelf: 'flex-end',
              ...buttonsWrapperSx,
            }}
            {...restButtonsWrapper}
          >
            {buttons}
          </Box>
        )}
      </Box>
    );
  },
);
